import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TransferStatusBadge } from "@/modules/inventory/presentation/ui/transfers/TransferStatusBadge";
import { transferItemTypeLabel } from "@/modules/inventory/presentation/ui/transfers/transferLaoLabels";
import {
  TRANSFER_CARD,
  TRANSFER_CARD_CONTENT_TABLE,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { TransferReportParams } from "@/modules/reports/presentation/api/client";
import {
  useTransferReportAgingQuery,
  useTransferReportByBranchQuery,
  useTransferReportByCategoryQuery,
  useTransferReportByItemQuery,
  useTransferReportLinesQuery,
} from "@/modules/reports/presentation/api/queries";
import { cn } from "@/shared/lib/utils";
import { Loader } from "@/shared/ui/Loader";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

type Props = {
  reportParams: TransferReportParams;
  linesOffset: number;
  linesLimit: number;
  onLinesPage: (offset: number) => void;
};

type ReportTab = "by-branch" | "by-category" | "by-item" | "aging" | "lines";

function fmtQty(n: number) {
  return n.toLocaleString("lo-LA", { maximumFractionDigits: 3 });
}

function TabCount({ n }: { n: number }) {
  return (
    <span className="bg-muted text-muted-foreground ml-1.5 rounded-md px-1.5 py-0.5 text-xs tabular-nums">
      {n.toLocaleString("lo-LA")}
    </span>
  );
}

export function TransferReportsContent({
  reportParams,
  linesOffset,
  linesLimit,
  onLinesPage,
}: Props) {
  const [tab, setTab] = useState<ReportTab>("by-branch");

  const byBranch = useTransferReportByBranchQuery(reportParams);
  const byCategory = useTransferReportByCategoryQuery(reportParams);
  const byItem = useTransferReportByItemQuery(reportParams);
  const aging = useTransferReportAgingQuery({ minAgeDays: 1, limit: 50 });
  const lines = useTransferReportLinesQuery({
    ...reportParams,
    limit: linesLimit,
    offset: linesOffset,
  });

  const branchRows = byBranch.data ?? [];
  const categoryRows = byCategory.data ?? [];
  const itemRows = byItem.data ?? [];
  const agingRows = aging.data ?? [];
  const lineItems = lines.data?.items ?? [];
  const lineTotal = lines.data?.total ?? 0;

  const tabLoading =
    (tab === "by-branch" && byBranch.isLoading) ||
    (tab === "by-category" && byCategory.isLoading) ||
    (tab === "by-item" && byItem.isLoading) ||
    (tab === "aging" && aging.isLoading) ||
    (tab === "lines" && lines.isLoading);

  return (
    <Card className={TRANSFER_CARD}>
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as ReportTab)}
        className="gap-0"
      >
        <div className="border-border border-b px-4 pt-4 pb-0">
          <TabsList variant="line" className="h-auto w-full justify-start gap-4 bg-transparent p-0">
            <TabsTrigger
              value="by-branch"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              ສະຫຼຸບຕາມສາຂາ
              {!byBranch.isLoading ? (
                <TabCount n={branchRows.length} />
              ) : null}
            </TabsTrigger>
            <TabsTrigger
              value="by-category"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              ຕາມຫມວດວັດຖຸດິບ
              {!byCategory.isLoading ? (
                <TabCount n={categoryRows.length} />
              ) : null}
            </TabsTrigger>
            <TabsTrigger
              value="by-item"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              ຕາມສິນຄ້າ
              {!byItem.isLoading ? <TabCount n={itemRows.length} /> : null}
            </TabsTrigger>
            <TabsTrigger
              value="aging"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              ຄຳຂໍຄ້າງ
              {!aging.isLoading ? <TabCount n={agingRows.length} /> : null}
            </TabsTrigger>
            <TabsTrigger
              value="lines"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              ລາຍລະອຽດບັນທັດ
              {!lines.isLoading ? <TabCount n={lineTotal} /> : null}
            </TabsTrigger>
          </TabsList>
        </div>

        <CardContent className={cn(TRANSFER_CARD_CONTENT_TABLE, "pt-0")}>
          {tabLoading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : null}

          <TabsContent value="by-branch" className="mt-0">
            {!tabLoading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ສາຂາ</TableHead>
                    <TableHead className="text-right">ຄຳຂໍ</TableHead>
                    <TableHead className="text-right">ບັນທັດ</TableHead>
                    <TableHead className="text-right">ຂໍ</TableHead>
                    <TableHead className="text-right">ສົ່ງ</TableHead>
                    <TableHead className="text-right">ຮັບ</TableHead>
                    <TableHead className="text-right">ຄວາມຕ່າງ</TableHead>
                    <TableHead className="text-right">ມູນຄ່າສົ່ງ (₭)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {branchRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-muted-foreground py-8 text-center"
                      >
                        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງທີ່ເລືອກ
                      </TableCell>
                    </TableRow>
                  ) : (
                    branchRows.map((row) => (
                      <TableRow key={row.branchId}>
                        <TableCell>
                          <span className="font-medium">{row.branchCode}</span>
                          <span className="text-muted-foreground ml-1 text-xs">
                            {row.branchName}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.requestCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.lineCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyRequested)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyShipped)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyReceived)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right tabular-nums",
                            Math.abs(row.qtyVariance) > 1e-6 &&
                              "text-amber-700 dark:text-amber-400",
                          )}
                        >
                          {fmtQty(row.qtyVariance)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.shippedValueLak.toLocaleString("lo-LA")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : null}
          </TabsContent>

          <TabsContent value="by-category" className="mt-0">
            {!tabLoading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ຫມວດ</TableHead>
                    <TableHead className="text-right">ຄຳຂໍ</TableHead>
                    <TableHead className="text-right">ບັນທັດ</TableHead>
                    <TableHead className="text-right">ຂໍ</TableHead>
                    <TableHead className="text-right">ສົ່ງ</TableHead>
                    <TableHead className="text-right">ຮັບ</TableHead>
                    <TableHead className="text-right">ຄວາມຕ່າງ</TableHead>
                    <TableHead className="text-right">ມູນຄ່າສົ່ງ (₭)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-muted-foreground py-8 text-center"
                      >
                        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງທີ່ເລືອກ
                      </TableCell>
                    </TableRow>
                  ) : (
                    categoryRows.map((row) => (
                      <TableRow
                        key={row.categoryId ?? "__uncategorized__"}
                      >
                        <TableCell className="font-medium">
                          {row.categoryName}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.requestCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.lineCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyRequested)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyShipped)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyReceived)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right tabular-nums",
                            Math.abs(row.qtyVariance) > 1e-6 &&
                              "text-amber-700 dark:text-amber-400",
                          )}
                        >
                          {fmtQty(row.qtyVariance)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.shippedValueLak.toLocaleString("lo-LA")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : null}
          </TabsContent>

          <TabsContent value="by-item" className="mt-0">
            {!tabLoading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ລາຍການ</TableHead>
                    <TableHead className="text-right">ຄຳຂໍ</TableHead>
                    <TableHead className="text-right">ບັນທັດ</TableHead>
                    <TableHead className="text-right">ຂໍ</TableHead>
                    <TableHead className="text-right">ສົ່ງ</TableHead>
                    <TableHead className="text-right">ຮັບ</TableHead>
                    <TableHead className="text-right">ຄວາມຕ່າງ</TableHead>
                    <TableHead className="text-right">ມູນຄ່າສົ່ງ (₭)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itemRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-muted-foreground py-8 text-center"
                      >
                        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງທີ່ເລືອກ
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemRows.map((row) => (
                      <TableRow key={`${row.itemType}:${row.itemId}:${row.unit}`}>
                        <TableCell>
                          <span className="font-medium">{row.itemName}</span>
                          <span className="text-muted-foreground ml-1 text-xs">
                            ({transferItemTypeLabel(row.itemType)} · {row.unit})
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.requestCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.lineCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyRequested)} {row.unit}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyShipped)} {row.unit}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(row.qtyReceived)} {row.unit}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right tabular-nums",
                            Math.abs(row.qtyVariance) > 1e-6 &&
                              "text-amber-700 dark:text-amber-400",
                          )}
                        >
                          {fmtQty(row.qtyVariance)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.shippedValueLak.toLocaleString("lo-LA")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : null}
          </TabsContent>

          <TabsContent value="aging" className="mt-0">
            {!tabLoading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ເລກທີ</TableHead>
                    <TableHead>ສາຂາ</TableHead>
                    <TableHead>ສະຖານະ</TableHead>
                    <TableHead className="text-right">ອາຍຸ (ວັນ)</TableHead>
                    <TableHead className="text-right">ບັນທັດ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agingRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-muted-foreground py-8 text-center"
                      >
                        ບໍ່ມີຄຳຂໍຄ້າງເກີນ 1 ວັນ
                      </TableCell>
                    </TableRow>
                  ) : (
                    agingRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Link
                            to="/app/inventory/transfers/$id"
                            params={{ id: row.id }}
                            className="font-mono text-primary hover:underline"
                          >
                            {row.requestNo}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {row.fromBranchCode} — {row.fromBranchName}
                        </TableCell>
                        <TableCell>
                          <TransferStatusBadge status={row.status} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.ageDays.toLocaleString("lo-LA")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.lineCount}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : null}
          </TabsContent>

          <TabsContent value="lines" className="mt-0">
            {!tabLoading ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ເລກທີ</TableHead>
                      <TableHead>ລາຍການ</TableHead>
                      <TableHead className="text-right">ຂໍ</TableHead>
                      <TableHead className="text-right">ສົ່ງ</TableHead>
                      <TableHead className="text-right">ຮັບ</TableHead>
                      <TableHead className="text-right">±</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-muted-foreground py-8 text-center"
                        >
                          ບໍ່ມີບັນທັດ
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineItems.map((row) => (
                        <TableRow key={row.lineId}>
                          <TableCell>
                            <Link
                              to="/app/inventory/transfers/$id"
                              params={{ id: row.transferId }}
                              className="font-mono text-xs text-primary hover:underline"
                            >
                              {row.requestNo}
                            </Link>
                            <div className="text-muted-foreground text-[10px]">
                              {row.fromBranchCode}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{row.itemName}</span>
                            <span className="text-muted-foreground ml-1 text-xs">
                              ({transferItemTypeLabel(row.itemType)}
                              {row.categoryName
                                ? ` · ${row.categoryName}`
                                : ""}
                              )
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {fmtQty(row.qtyRequested)} {row.unit}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {row.qtyShipped != null
                              ? `${fmtQty(row.qtyShipped)} ${row.unit}`
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {row.qtyReceived != null
                              ? `${fmtQty(row.qtyReceived)} ${row.unit}`
                              : "—"}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "text-right tabular-nums text-sm",
                              Math.abs(row.qtyVariance) > 1e-6 &&
                                "text-amber-700 dark:text-amber-400",
                            )}
                          >
                            {fmtQty(row.qtyVariance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
                  <span className="text-muted-foreground">
                    {lineTotal.toLocaleString("lo-LA")} ບັນທັດ
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={linesOffset <= 0}
                      onClick={() =>
                        onLinesPage(Math.max(0, linesOffset - linesLimit))
                      }
                    >
                      ກ່ອນໜ້າ
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={linesOffset + linesLimit >= lineTotal}
                      onClick={() => onLinesPage(linesOffset + linesLimit)}
                    >
                      ຖັດໄປ
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
}
